/**
 * composables/useComposerVoice.ts —— Composer 语音位（**整件重抄自 102 `composables/useComposerVoice.js`(175 行)**，
 * Vue→React；旧面 `useChatVoice.js` 迁移件）。依据：设计件 §2.4 去向表 L314（`useChatVoice` ⇒ 并入 Composer 语音位，
 * 状态进 **binding 局部态**，功能不得蒸发）／§11.1 `POST /api/stt`（50006 服务不可用／40001 音频太短）。
 *
 * 与旧面的三处**有意差异**（逐条登记，沿用 102 报告口径）：
 *   ① **状态局部化**：旧面读写全局单例三态 ⇒ 本件全为**本 hook 局部 state**（不进全局 store，对本仓全局态零引用）；
 *   ② **识别去向**（承大王口径「按住按钮识别，松开直接发送」）：识别成功 ⇒ 回调 `onRecognized(text)`，
 *      且回调点**在 `isProcessing` 复位之后**（否则调用方在「识别中」被 canSubmit 守卫挡住 ⇒ 静默不发）；
 *   ③ **失败呈现**：一律经注入的 `notify(text, kind)`（Composer 行内 notice，role=alert）——**禁静默降级**。
 *
 * 🔴 出网面（本仓硬约束）：`transport/whitelist.ts` 白名单＝`EGRESS_PATHS` 逐条声明（当前 9 条：`/stream`·
 *   `/events`·`/api/auth/{login,logout,me}`·`/api/sessions`·`/api/models`·`/api/files`·`/api/stt`）；
 *   `/api/stt` **已放行**（★FE2 L-FE2-3）——`sttApi` 仍是**注入面**（单测替身用），且**不是**静默空实现：
 *   显式置空（`null`）⇒ 显式未接线文案（notify，不谎报可用）。A8（后端能力对账）三项现势：
 *   ① 后端 `POST /api/stt`（FunASR，50006/40001 同义 503/400）**已落** ② 白名单增一条（**已落**）
 *   ③ 前端接线（**已落**——★FE-E3 账行 `L-PF-ASR-1`：`transport/stt.ts` 客户端 ＋ `Composer` 语音位
 *   **缺省注入实装**；载体形态＝**JSON 单形**（★FE-E3 账行 `L-PF-ASR-2` 定案，`AUDIO_CARRIER`））。
 *
 * 依赖注入面（单测用；缺省＝真浏览器 API）：`sttApi`／`mediaApi`／`recorderFactory`／`isSecureContext`。
 * 语义逐条迁移：模式互斥（录音/处理中禁切换）、按住-松开、getUserMedia 竞态旗标（松开早于授权 ⇒ 回来即停）、
 *   mime 三级回退、短录音 <8000B 护栏、识别空文本提示、recorder.onerror 清态、权限/设备/安全上下文四档显式提示。
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** 短录音护栏阈值（旧面 `useChatVoice.js:56` 同值：总字节 <8000 视为太短） */
export const MIN_AUDIO_BYTES = 8000;

export interface SttResult { ok: boolean; text?: string; errorText?: string; /** 服务端/本地显式码（旧同义码 50006/40001） */ code?: string | null }

/** 显式码（册 §6.6 语音接线口径；与 `transport/stt.ts::legacyCodeOf` 同源同值） */
export const STT_CODE_ENGINE_ABSENT = "50006";
export const STT_CODE_AUDIO_SHORT = "40001";

/** 带码提示（★F-05 显式码形：`code` 可机判；`text` 为人读文案） */
export interface VoiceNotice {
  /** 显式码（50006 引擎不可用／40001 音频空或过短） */
  code: string;
  /** 人读文案 */
  text: string;
  /** 提示档（缺省 error —— 带码提示都是「没转成」的显式说明） */
  kind?: "info" | "error";
}

/** 提示出口签名（双形：旧字符串形 ＋ ★F-05 带码对象形） */
export type NotifyFn = {
  (text: string, kind?: "info" | "error"): void;
  (notice: VoiceNotice): void;
};
export interface VoiceDeps {
  /** STT 通道；**缺省 null ⇒ 显式未接线**（禁静默） */
  sttApi?: ((blob: Blob) => Promise<SttResult>) | null;
  mediaApi?: { getUserMedia: (c: { audio: boolean }) => Promise<MediaStream> } | null;
  recorderFactory?: (new (s: MediaStream, o: { mimeType: string }) => MediaRecorder) | null;
  isSecureContext?: () => boolean;
  /** 若提供 getSessionId，则随识别请求带上会话归属（本仓提交时刻取值口径） */
  getSessionId?: (() => string) | null;
}
export interface UseComposerVoiceOptions extends VoiceDeps {
  /**
   * 提示出口。两种形态（**兼容旧字符串形**，勿改名破坏既有注入面）：
   *   · `notify("文案")` / `notify("文案","error")` —— 旧面；
   *   · `notify({code, text})` —— ★F-05 显式码形（册 §6.6：50006／40001 必须带码，可机判）。
   */
  notify?: NotifyFn;
  onRecognized?: (text: string) => void;
  mintBlob?: (chunks: BlobPart[], type: string) => Blob;
}

export interface ComposerVoice {
  voiceMode: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  busy: boolean;
  lastError: string;
  toggleVoiceMode: () => void;
  startRecording: () => void;
  stopRecording: () => void;
  dispose: () => void;
}

export function useComposerVoice(opts: UseComposerVoiceOptions = {}): ComposerVoice {
  const {
    sttApi = null,
    mediaApi = null,
    recorderFactory = null,
    isSecureContext = () => typeof window !== "undefined" && window.isSecureContext === true,
    notify = () => {},
    mintBlob = (chunks: BlobPart[], type: string) => new Blob(chunks, { type }),
  } = opts;

  const [voiceMode, setVoiceMode] = useState(false);       // 🔴 局部态（不写全局 store）
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastError, setLastError] = useState("");
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const stopRequestedRef = useRef(false);                  // 旧面竞态旗标语义
  const disposedRef = useRef(false);

  const devices = (): VoiceDeps["mediaApi"] | null => mediaApi ?? ((typeof navigator !== "undefined" && navigator.mediaDevices) || null);
  const RecorderCtor = () => recorderFactory ?? ((typeof window !== "undefined" && (window.MediaRecorder as never)) || null);

  const fail = (text: string, code?: string): void => {
    setLastError(text);
    if (code) notify({ code, text });
    else notify(text, "error");
  };

  const toggleVoiceMode = useCallback((): void => {
    setVoiceMode((v) => {
      if (isRecordingRef.current || processingRef.current) return v;   // 旧面 L17：录音/处理中禁切换
      return !v;
    });
  }, []);
  // 供 toggleVoiceMode 读到的即时值（避免闭包旧值）
  const isRecordingRef = useRef(false);
  const processingRef = useRef(false);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { processingRef.current = isProcessing; }, [isProcessing]);

  const pickMime = (): string => {
    const Rec = RecorderCtor() as unknown as { isTypeSupported?: (t: string) => boolean } | null;
    const supported = Rec && typeof Rec.isTypeSupported === "function" ? Rec.isTypeSupported.bind(Rec) : () => false;
    if (supported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
    if (supported("audio/webm")) return "audio/webm";
    return "audio/wav";                                   // 旧面 L41-45 三级回退同序
  };

  const cleanup = (): void => { recRef.current = null; chunksRef.current = []; };

  const startRecording = useCallback((): void => {
    if (isRecordingRef.current) return;
    const md = devices();
    if (!md || typeof md.getUserMedia !== "function") {
      fail(isSecureContext()
        ? "您的浏览器不支持录音功能，请使用 Chrome/Edge/Firefox 最新版"
        : "语音输入需要安全连接，请使用 https:// 地址访问本站");     // 旧面 L23-30 两档
      return;
    }
    stopRequestedRef.current = false;
    setIsRecording(true); isRecordingRef.current = true;
    void (async () => {
      try {
        const stream = await md.getUserMedia({ audio: true });
        if (disposedRef.current || stopRequestedRef.current) {     // 竞态：授权回来前已松开 ⇒ 立即停轨退出
          stream.getTracks().forEach((t) => t.stop());
          setIsRecording(false); isRecordingRef.current = false;
          return;
        }
        chunksRef.current = [];
        const mimeType = pickMime();
        const Rec = RecorderCtor();
        if (!Rec) { fail("当前环境不支持 MediaRecorder，无法录音"); setIsRecording(false); isRecordingRef.current = false; return; }
        const recorder = new (Rec as unknown as new (s: MediaStream, o: { mimeType: string }) => MediaRecorder)(stream, { mimeType });
        recRef.current = recorder;
        recorder.ondataavailable = (e: BlobEvent) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const total = chunksRef.current.reduce((s: number, c) => s + ((c as Blob).size || 0), 0);
          if (total < MIN_AUDIO_BYTES) {
            setIsRecording(false); isRecordingRef.current = false;
            notify("录音时间太短，已取消", "info");                  // 旧面 L57 显式（info）
            cleanup();
            return;
          }
          setIsRecording(false); isRecordingRef.current = false;
          setIsProcessing(true); processingRef.current = true;
          void (async () => {
            let recognized = "";
            try {
              if (typeof sttApi !== "function") {
                // 🔴 通道**显式未注入**（`sttApi: null`）：显式说明（不谎报可用、不静默）。
                //   ★F-05（册 §6.6 承 `L-PF-ASR-1`）：生产路径缺省＝`transport/stt.ts` 实装
                //   （`Composer` 语音位注入 `sttApi: props.sttApi ?? sttTransport`）
                //   ⇒ 本支仅在注入面被**显式置空**时可达（单测替身）；码面＝**50006**（引擎不可用，旧同义）。
                notify({
                  code: STT_CODE_ENGINE_ABSENT,
                  text: "语音识别通道未注入（sttApi 为空），无法转写：请由宿主注入实现（生产＝`transport/stt.ts`）",
                });
              } else {
                const r = await sttApi(mintBlob(chunksRef.current, mimeType));
                if (r && r.ok === true && typeof r.text === "string" && r.text.trim()) {
                  setLastError(""); recognized = r.text;
                } else if (r && r.ok === true) {
                  // 空转写（引擎在位但无内容）：显式 info（不编造文本、不静默）
                  notify({ code: STT_CODE_AUDIO_SHORT, text: "未能识别到语音内容" });
                } else {
                  // 失败：优先带服务端/传输面显式码（503⇒50006／400⇒40001），无码则显式文案兜底
                  fail((r && r.errorText) || "语音识别失败，请重试",
                       (r && typeof r.code === "string" && r.code) || undefined);
                }
              }
            } finally {
              setIsProcessing(false); processingRef.current = false;
              cleanup();
            }
            // 🔴 回调**移到识别完成之后**（isProcessing 已复位）⇒ 消费方直接提交不被守卫挡住
            if (recognized && typeof opts.onRecognized === "function") opts.onRecognized(recognized);
          })();
        };
        recorder.onerror = () => {                                   // 旧面 L89-96：显式＋清态
          fail("录音出错");
          setIsRecording(false); isRecordingRef.current = false;
          setIsProcessing(false); processingRef.current = false;
          stream.getTracks().forEach((t) => t.stop());
          cleanup();
        };
        recorder.start(250);                                         // 旧面 L98 分片周期同值
      } catch (e) {
        setIsRecording(false); isRecordingRef.current = false;
        const name = (e as { name?: string } | null)?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") fail("麦克风权限被拒绝，请在浏览器设置中允许麦克风访问");
        else if (name === "NotFoundError") fail("未检测到麦克风设备");
        else fail("启动录音失败");                                    // 旧面 L101-108 四档同序
      }
    })();
  }, [mediaApi, recorderFactory, isSecureContext, notify, mintBlob, sttApi, opts.onRecognized]);

  const stopRecording = useCallback((): void => {
    stopRequestedRef.current = true;                                 // 授权在途也置旗标
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") { try { rec.stop(); } catch { /* onstop 未及 ⇒ cleanup 兜底 */ } }
  }, []);

  const dispose = useCallback((): void => {
    if (disposedRef.current) return;
    disposedRef.current = true;
    stopRequestedRef.current = true;
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") { try { rec.stop(); } catch { /* noop */ } }
    cleanup();
    setIsRecording(false); isRecordingRef.current = false;
    setIsProcessing(false); processingRef.current = false;
  }, []);

  return { voiceMode, isRecording, isProcessing, busy: isRecording || isProcessing, lastError, toggleVoiceMode, startRecording, stopRecording, dispose };
}

export default useComposerVoice;
